import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { PartnersSection } from './components/PartnersSection';
import { FinalFooter } from './components/FinalFooter';
import { Toast } from './components/Toast';
import { CookieConsent } from './components/CookieConsent';
import { NotificationPopup } from './components/NotificationPopup';

import { HomePage } from './pages/HomePage';
import { FilterPage } from './pages/FilterPage';
import { TutorsPage } from './pages/TutorsPage';
import { LoginPage } from './pages/LoginPage';
import { UnifiedSignupPage } from './pages/UnifiedSignupPage';
import { SimpleSignupPage } from './pages/SimpleSignupPage';
import { SignupSuccessPage } from './pages/SignupSuccessPage';
import { RoleSelectionPage } from './pages/RoleSelectionPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { VerifyOtpPage } from './pages/VerifyOtpPage';
import { CreateProfilePage } from './pages/CreateProfilePage';
import { CreateStudentProfilePage } from './pages/CreateStudentProfilePage';
import { EmailConfirmationPage } from './pages/EmailConfirmationPage';
import { EmailNotConfirmedPage } from './components/EmailNotConfirmedPage';
import { ProfilePage } from './pages/ProfilePage';
import { EditProfilePage } from './pages/EditProfilePage';
import { AvailabilityPage } from './pages/AvailabilityPage';
import { SubjectsPage } from './pages/SubjectsPage';
import { SecurityPage } from './pages/SecurityPage';
import { ContactRequestsPage } from './pages/ContactRequestsPage';
import { HelpPage } from './pages/HelpPage';
import { ContactPage } from './pages/ContactPage';
import { FAQPage } from './pages/FAQPage';
import { TermsPage } from './pages/TermsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { ProfileRequiredRoute } from './components/ProfileRequiredRoute';
import { useToast } from './hooks/useToast';
import './App.css';

function App() {
  const { toasts, showToast, removeToast } = useToast();

  return (
    <AuthProvider>
      <NotificationProvider>
    <Router>
      <div className="min-h-screen flex flex-col">
        <Header showToast={showToast} />
            <NotificationPopup />
        
        <main className="flex-grow">
          <Routes>
              {/* Pages publiques */}
            <Route path="/" element={<HomePage showToast={showToast} />} />
            <Route path="/filter" element={<FilterPage showToast={showToast} />} />
            <Route path="/tutors" element={<TutorsPage showToast={showToast} />} />
              <Route path="/aide" element={<HelpPage />} />
              <Route path="/how-it-works" element={<HelpPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/conditions" element={<TermsPage />} />
              <Route path="/confidentialite" element={<PrivacyPage />} />
              
              {/* Pages d'authentification */}
              <Route path="/login" element={<LoginPage showToast={showToast} />} />
              <Route path="/signup" element={<SimpleSignupPage showToast={showToast} />} />
              <Route path="/signup-success" element={<SignupSuccessPage />} />
              <Route path="/role-selection" element={
                <ProtectedRoute>
                  <RoleSelectionPage showToast={showToast} />
                </ProtectedRoute>
              } />
              <Route path="/become-tutor" element={<UnifiedSignupPage showToast={showToast} />} />
              <Route path="/reset-password" element={<ResetPasswordPage showToast={showToast} />} />
              
              {/* Pages protégées avec profil obligatoire */}
              <Route path="/create-profile" element={
                <ProtectedRoute>
                  <CreateProfilePage showToast={showToast} />
                </ProtectedRoute>
              } />
              
              <Route path="/create-student-profile" element={
                <ProtectedRoute>
                  <CreateStudentProfilePage showToast={showToast} />
                </ProtectedRoute>
              } />
              
              <Route path="/profile" element={
                <ProfileRequiredRoute>
                  <ProfilePage showToast={showToast} />
                </ProfileRequiredRoute>
              } />
              
              <Route path="/profile/edit" element={
                <ProfileRequiredRoute>
                  <EditProfilePage showToast={showToast} />
                </ProfileRequiredRoute>
              } />
              
              <Route path="/profile/availability" element={
                <ProfileRequiredRoute>
                  <AvailabilityPage showToast={showToast} />
                </ProfileRequiredRoute>
              } />
              
              <Route path="/profile/subjects" element={
                <ProfileRequiredRoute>
                  <SubjectsPage showToast={showToast} />
                </ProfileRequiredRoute>
              } />
              
              <Route path="/profile/security" element={
                <ProtectedRoute>
                  <SecurityPage showToast={showToast} />
                </ProtectedRoute>
              } />
              
              <Route path="/profile/requests" element={
                <ProfileRequiredRoute>
                  <ContactRequestsPage showToast={showToast} />
                </ProfileRequiredRoute>
              } />
              
              {/* Pages de confirmation */}
              <Route path="/email-confirmed" element={
                <EmailConfirmationPage showToast={showToast} />
              } />
              
              <Route path="/email-not-confirmed" element={
                <EmailNotConfirmedPage showToast={showToast} />
              } />
              
              <Route path="/email-confirmation" element={
                <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4">
                  <div className="max-w-md w-full bg-white rounded-xl shadow-lg p-8 text-center">
                    <div className="mx-auto h-16 w-16 bg-green-600 rounded-full flex items-center justify-center mb-4">
                      <i className="fas fa-envelope-check text-white text-2xl"></i>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">
                      Vérifiez votre email
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Nous avons envoyé un lien de confirmation à votre adresse email. 
                      Cliquez sur le lien pour activer votre compte et créer votre profil tuteur.
                    </p>
                    <div className="bg-[#002c46]/5 border border-[#003d5c]/20 rounded-lg p-4 mb-6">
                      <div className="flex items-center">
                        <i className="fas fa-info-circle text-[#002c46] mr-2"></i>
                        <p className="text-sm text-[#001a2a]">
                          Une fois votre email confirmé, vous serez automatiquement redirigé pour créer votre profil tuteur.
                        </p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <button
                        onClick={() => window.location.href = '/login'}
                        className="w-full py-3 px-4 bg-[#002c46] text-white font-semibold rounded-lg hover:bg-[#001a2a] transition-colors"
                      >
                        <i className="fas fa-sign-in-alt mr-2"></i>
                        Se connecter
                      </button>
                      <button
                        onClick={() => window.location.href = '/'}
                        className="w-full py-3 px-4 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <i className="fas fa-home mr-2"></i>
                        Retour à l'accueil
                      </button>
                    </div>
                  </div>
                </div>
              } />
              
              {/* Page d'oubli de mot de passe */}
              <Route path="/forgot-password" element={
                <ForgotPasswordPage showToast={showToast} />
              } />
              
              {/* Page de vérification OTP (code à 6 chiffres) */}
              <Route path="/verify-otp" element={
                <VerifyOtpPage showToast={showToast} />
              } />
              
              {/* Dashboard (page temporaire) */}
              <Route path="/dashboard" element={
                <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center py-12 px-4">
                  <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg p-8 text-center">
                    <div className="mx-auto h-16 w-16 bg-green-600 rounded-full flex items-center justify-center mb-4">
                      <i className="fas fa-check-circle text-white text-2xl"></i>
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                      🎉 Bienvenue dans la communauté !
                    </h2>
                    <p className="text-gray-600 mb-6">
                      Votre profil tuteur a été créé avec succès. Vous pouvez maintenant aider les étudiants 
                      et commencer à gagner de l'argent en partageant vos connaissances.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <div className="bg-[#002c46]/5 p-4 rounded-lg">
                        <i className="fas fa-users text-[#002c46] text-2xl mb-2"></i>
                        <h3 className="font-semibold text-gray-900">Connectez-vous</h3>
                        <p className="text-sm text-gray-600">Répondez aux demandes d'aide des étudiants</p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <i className="fas fa-money-bill-wave text-green-600 text-2xl mb-2"></i>
                        <h3 className="font-semibold text-gray-900">Gagnez de l'argent</h3>
                        <p className="text-sm text-gray-600">Fixez vos tarifs et gérez vos horaires</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <button
                        onClick={() => window.location.href = '/'}
                        className="w-full py-3 px-4 bg-[#002c46] text-white font-semibold rounded-lg hover:bg-[#001a2a] transition-colors"
                      >
                        <i className="fas fa-home mr-2"></i>
                        Retour à l'accueil
                      </button>
                      <p className="text-sm text-gray-500">
                        Le dashboard complet sera bientôt disponible avec gestion des sessions, 
                        statistiques et messagerie.
                      </p>
                    </div>
                  </div>
                </div>
              } />
              

          </Routes>
        </main>
        
        <Footer />
        <PartnersSection />
        <FinalFooter />
        <Toast toasts={toasts} onRemove={removeToast} />
          <CookieConsent />

      </div>
    </Router>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App; 